import React, { useState, useEffect } from 'react';
import { api } from '../App';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { Star, MessageSquare, Send, Users } from 'lucide-react';

export default function ReviewsTab() {
  const [reviews, setReviews] = useState([]);
  const [givenReviews, setGivenReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [replyingTo, setReplyingTo] = useState(null);
  const [replyText, setReplyText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [activeSection, setActiveSection] = useState('received');

  useEffect(() => { fetchAllReviews(); }, []);

  const fetchAllReviews = async () => {
    try {
      const [received, given] = await Promise.all([
        api.get('/providers/me/reviews'),
        api.get('/providers/me/reviews-given'),
      ]);
      setReviews(received.data || []);
      setGivenReviews(given.data || []);
    } catch { toast.error('Failed to load reviews'); }
    finally { setLoading(false); }
  };

  const submitResponse = async (reviewId) => {
    if (!replyText.trim()) return;
    setSubmitting(true);
    try {
      await api.post(`/reviews/${reviewId}/respond`, { text: replyText.trim() });
      toast.success('Response published');
      setReplyingTo(null);
      setReplyText('');
      fetchAllReviews();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit response');
    } finally { setSubmitting(false); }
  };

  if (loading) return <div className="flex justify-center py-16"><div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-[#D4AF37]" /></div>;

  const avgRating = reviews.length > 0
    ? (reviews.reduce((sum, r) => sum + r.rating, 0) / reviews.length).toFixed(1)
    : '0.0';

  return (
    <div className="space-y-6" data-testid="reviews-tab">
      {/* Section toggle */}
      <div className="flex gap-2">
        <Button
          variant={activeSection === 'received' ? 'default' : 'outline'}
          className={activeSection === 'received' ? 'btn-primary' : 'border-[#E6E4DD]'}
          onClick={() => setActiveSection('received')}
          data-testid="received-reviews-tab"
        >
          <Star className="h-4 w-4 mr-1" />Received ({reviews.length})
        </Button>
        <Button
          variant={activeSection === 'given' ? 'default' : 'outline'}
          className={activeSection === 'given' ? 'btn-primary' : 'border-[#E6E4DD]'}
          onClick={() => setActiveSection('given')}
          data-testid="given-reviews-tab"
        >
          <Users className="h-4 w-4 mr-1" />Given ({givenReviews.length})
        </Button>
      </div>

      {activeSection === 'received' && (
        <>
          {reviews.length === 0 ? (
            <div className="text-center py-16" data-testid="reviews-empty">
              <Star className="h-12 w-12 text-stone-300 mx-auto mb-3" />
              <h3 className="font-heading text-lg text-[#1A1A1A] mb-1">No reviews yet</h3>
              <p className="text-sm text-stone-500">Reviews will appear here once clients leave feedback after their appointments.</p>
            </div>
          ) : (
            <>
              {/* Summary */}
              <div className="card-surface p-6 flex items-center gap-6">
                <div className="text-center">
                  <p className="text-3xl font-heading font-bold text-[#D4AF37]">{avgRating}</p>
                  <div className="flex items-center gap-0.5 justify-center mt-1">
                    {[...Array(5)].map((_, starIdx) => (
                      <Star key={`avg-star-${starIdx}`} className={`h-4 w-4 ${starIdx < Math.round(avgRating) ? 'text-[#D4AF37] fill-current' : 'text-stone-300'}`} />
                    ))}
                  </div>
                  <p className="text-xs text-stone-500 mt-1">{reviews.length} review{reviews.length !== 1 ? 's' : ''}</p>
                </div>
                <div className="flex-1 space-y-1">
                  {[5,4,3,2,1].map(star => {
                    const count = reviews.filter(r => r.rating === star).length;
                    const pct = reviews.length > 0 ? (count / reviews.length) * 100 : 0;
                    return (
                      <div key={star} className="flex items-center gap-2 text-sm">
                        <span className="w-8 text-stone-500 text-right">{star}</span>
                        <Star className="h-3 w-3 text-[#D4AF37] fill-current" />
                        <div className="flex-1 h-2 bg-[#F9F6F0] rounded-full overflow-hidden">
                          <div className="h-full bg-[#D4AF37] rounded-full transition-all" style={{ width: `${pct}%` }} />
                        </div>
                        <span className="w-8 text-xs text-stone-400">{count}</span>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Review list */}
              <div className="space-y-4">
                {reviews.map(review => (
                  <div key={review.review_id} className="card-surface p-5" data-testid={`review-${review.review_id}`}>
                    <div className="flex items-center gap-3 mb-2">
                      <div className="w-10 h-10 rounded-full bg-[#F9F6F0] flex items-center justify-center text-[#D4AF37] font-medium">
                        {review.client_name?.charAt(0) || '?'}
                      </div>
                      <div className="flex-1">
                        <p className="font-medium text-[#1A1A1A] text-sm">{review.client_name || 'Client'}</p>
                        <div className="flex items-center gap-0.5">
                          {[...Array(5)].map((_, starIdx) => (
                            <Star key={`review-star-${starIdx}`} className={`h-3.5 w-3.5 ${starIdx < review.rating ? 'text-[#D4AF37] fill-current' : 'text-stone-300'}`} />
                          ))}
                        </div>
                      </div>
                      {review.created_at && (
                        <span className="text-xs text-stone-400">{new Date(review.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}</span>
                      )}
                    </div>

                    {review.text && <p className="text-stone-600 text-sm mb-3">{review.text}</p>}

                    {/* Provider response */}
                    {review.provider_response ? (
                      <div className="ml-4 pl-4 border-l-2 border-[#D4AF37] bg-[#F9F6F0] rounded-r-lg p-3">
                        <p className="text-xs font-medium text-[#D4AF37] mb-1">Your response</p>
                        <p className="text-sm text-stone-600">{review.provider_response.text}</p>
                        {review.provider_response.responded_at && (
                          <p className="text-xs text-stone-400 mt-1">{new Date(review.provider_response.responded_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}</p>
                        )}
                      </div>
                    ) : (
                      <>
                        {replyingTo === review.review_id ? (
                          <div className="mt-2 space-y-2" data-testid={`reply-form-${review.review_id}`}>
                            <Textarea
                              value={replyText}
                              onChange={e => setReplyText(e.target.value)}
                              placeholder="Write a public response..."
                              className="input-zuriya h-20 resize-none"
                              maxLength={500}
                              data-testid="reply-textarea"
                            />
                            <div className="flex items-center justify-between">
                              <span className="text-xs text-stone-400">{replyText.length}/500</span>
                              <div className="flex gap-2">
                                <Button variant="ghost" size="sm" onClick={() => { setReplyingTo(null); setReplyText(''); }}>Cancel</Button>
                                <Button
                                  size="sm"
                                  className="btn-primary gap-1"
                                  onClick={() => submitResponse(review.review_id)}
                                  disabled={!replyText.trim() || submitting}
                                  data-testid="submit-reply-btn"
                                >
                                  <Send className="h-3.5 w-3.5" />
                                  {submitting ? 'Sending...' : 'Respond'}
                                </Button>
                              </div>
                            </div>
                          </div>
                        ) : (
                          <button
                            onClick={() => { setReplyingTo(review.review_id); setReplyText(''); }}
                            className="mt-2 text-sm text-[#D4AF37] hover:underline flex items-center gap-1"
                            data-testid={`reply-btn-${review.review_id}`}
                          >
                            <MessageSquare className="h-3.5 w-3.5" />
                            Respond to review
                          </button>
                        )}
                      </>
                    )}
                  </div>
                ))}
              </div>
            </>
          )}
        </>
      )}

      {activeSection === 'given' && (
        <>
          {givenReviews.length === 0 ? (
            <div className="text-center py-16" data-testid="given-reviews-empty">
              <Users className="h-12 w-12 text-stone-300 mx-auto mb-3" />
              <h3 className="font-heading text-lg text-[#1A1A1A] mb-1">No client reviews yet</h3>
              <p className="text-sm text-stone-500">Reviews you leave for clients after completed bookings will appear here.</p>
            </div>
          ) : (
            <div className="space-y-4">
              {givenReviews.map(review => (
                <div key={review.review_id} className="card-surface p-5" data-testid={`given-review-${review.review_id}`}>
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-10 h-10 rounded-full bg-[#F9F6F0] flex items-center justify-center text-stone-500 font-medium">
                      {review.client_name?.charAt(0) || '?'}
                    </div>
                    <div className="flex-1">
                      <p className="font-medium text-[#1A1A1A] text-sm">{review.client_name || 'Client'}</p>
                      <div className="flex items-center gap-0.5">
                        {[...Array(5)].map((_, starIdx) => (
                          <Star key={`given-star-${starIdx}`} className={`h-3.5 w-3.5 ${starIdx < review.rating ? 'text-[#D4AF37] fill-current' : 'text-stone-300'}`} />
                        ))}
                      </div>
                    </div>
                    {review.created_at && (
                      <span className="text-xs text-stone-400">{new Date(review.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}</span>
                    )}
                  </div>
                  {review.text && <p className="text-stone-600 text-sm">{review.text}</p>}
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
